<template>
  <div class="p-6 max-w-5xl mx-auto">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900">Policies &amp; Documents</h1>
        <p class="text-sm text-gray-500 mt-1">Manage the terms, policies and documents shown to invited users, per app.</p>
      </div>
    </div>

    <!-- App selector -->
    <div class="flex flex-wrap items-center gap-2 mb-6">
      <button
        v-for="app in apps"
        :key="app.id"
        @click="selectApp(app)"
        :class="['px-3 py-1.5 rounded text-sm border',
                 selectedApp && selectedApp.id === app.id
                   ? 'bg-blue-900 text-white border-blue-900'
                   : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50']">
        {{ app.name }}
        <span class="ml-1 text-xs opacity-70">({{ app.documents_count }})</span>
      </button>
      <button @click="showAddApp = !showAddApp"
              class="px-3 py-1.5 rounded text-sm border border-dashed border-gray-400 text-gray-600 hover:bg-gray-50">
        <i class="fas fa-plus mr-1"></i> Add app
      </button>
    </div>

    <!-- Add app -->
    <form v-if="showAddApp" @submit.prevent="createApp" class="bg-white border border-gray-200 rounded p-4 mb-6 flex flex-wrap gap-3 items-end">
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">App name</label>
        <input v-model="newApp.name" required placeholder="e.g. DOQSynth"
               class="border border-gray-300 rounded px-3 py-1.5 text-sm" />
      </div>
      <div class="flex-1 min-w-[200px]">
        <label class="block text-xs font-medium text-gray-600 mb-1">Description</label>
        <input v-model="newApp.description" placeholder="Optional"
               class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
      </div>
      <button type="submit" class="bg-blue-900 text-white text-sm px-4 py-1.5 rounded hover:bg-blue-800">Create</button>
    </form>

    <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">{{ error }}</div>

    <template v-if="selectedApp">
      <!-- Public portal link for this app -->
      <div class="bg-blue-50 border border-blue-200 rounded p-4 mb-6 flex flex-wrap items-center gap-3">
        <div class="flex-1 min-w-[240px]">
          <p class="text-xs font-medium text-blue-900 mb-1">Public portal link — {{ selectedApp.name }}</p>
          <code class="text-sm text-blue-800 break-all">{{ portalUrl }}</code>
          <p class="text-xs text-blue-700/70 mt-1">Share this with clients to view all published resources online (no login).</p>
        </div>
        <div class="flex items-center gap-2">
          <a :href="portalUrl" target="_blank" rel="noopener"
             class="text-sm px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-100">Open</a>
          <button type="button" @click="copyPortalLink"
                  class="text-sm px-3 py-1.5 rounded bg-blue-900 text-white hover:bg-blue-800 w-24">
            {{ copied ? 'Copied!' : 'Copy link' }}
          </button>
        </div>
      </div>

      <!-- Upload form -->
      <div class="bg-white border border-gray-200 rounded p-4 mb-6">
        <h2 class="font-medium text-gray-800 mb-3">Add a document to {{ selectedApp.name }}</h2>
        <form @submit.prevent="createDocument" class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input v-model="form.title" required class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select v-model="form.category" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
              <option v-for="c in categories" :key="c" :value="c">{{ label(c) }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Version</label>
            <input v-model="form.version" placeholder="e.g. v1.1" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Effective date</label>
            <input v-model="form.effective_on" type="date" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
          </div>
          <div class="md:col-span-2">
            <label class="block text-xs font-medium text-gray-600 mb-1">File (PDF / DOCX / HTML)</label>
            <input type="file" @change="onFile" class="w-full text-sm" />
            <p class="text-xs text-gray-400 mt-1">…or provide an external link instead:</p>
            <input v-model="form.external_url" placeholder="https://…" class="w-full border border-gray-300 rounded px-3 py-1.5 text-sm mt-1" />
          </div>
          <div class="flex items-center gap-2">
            <input id="published" type="checkbox" v-model="form.published" />
            <label for="published" class="text-sm text-gray-700">Publish immediately (visible to users)</label>
          </div>
          <div class="md:col-span-2">
            <button type="submit" :disabled="saving"
                    class="bg-blue-900 text-white text-sm px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50">
              {{ saving ? 'Uploading…' : 'Add document' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Documents table -->
      <div class="bg-white border border-gray-200 rounded overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-gray-500 text-left text-xs uppercase">
            <tr>
              <th class="px-4 py-2">Title</th>
              <th class="px-4 py-2">Category</th>
              <th class="px-4 py-2">Version</th>
              <th class="px-4 py-2">Status</th>
              <th class="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="doc in documents" :key="doc.id" class="border-t border-gray-100">
              <td class="px-4 py-2">
                <a v-if="doc.download_path || doc.external_url" :href="link(doc)" target="_blank" class="text-blue-700 hover:underline">{{ doc.title }}</a>
                <span v-else>{{ doc.title }}</span>
                <div v-if="doc.filename" class="text-xs text-gray-400">{{ doc.filename }}</div>
              </td>
              <td class="px-4 py-2">{{ label(doc.category) }}</td>
              <td class="px-4 py-2">{{ doc.version || '—' }}</td>
              <td class="px-4 py-2">
                <span :class="doc.published ? 'text-green-700' : 'text-gray-400'">
                  {{ doc.published ? 'Published' : 'Draft' }}
                </span>
              </td>
              <td class="px-4 py-2 text-right whitespace-nowrap">
                <button @click="togglePublish(doc)" class="text-xs text-blue-700 hover:underline mr-3">
                  {{ doc.published ? 'Unpublish' : 'Publish' }}
                </button>
                <button @click="removeDocument(doc)" class="text-xs text-red-600 hover:underline">Delete</button>
              </td>
            </tr>
            <tr v-if="documents.length === 0">
              <td colspan="5" class="px-4 py-6 text-center text-gray-400">No documents yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import appDocumentService from '../utils/appDocumentService'
import onboardingService from '../utils/onboardingService'

const categories = ['terms', 'policy', 'document', 'manual', 'guide']
const apps = ref([])
const selectedApp = ref(null)
const documents = ref([])
const showAddApp = ref(false)
const newApp = ref({ name: '', description: '' })
const saving = ref(false)
const error = ref(null)

const blankForm = () => ({ title: '', category: 'policy', version: '', effective_on: '', external_url: '', published: true, file: null })
const form = ref(blankForm())

function label (c) { return c.charAt(0).toUpperCase() + c.slice(1) }
function link (doc) { return onboardingService.documentUrl(doc) }

// Public portal link for the selected app (share via any channel).
const copied = ref(false)
const portalUrl = computed(() =>
  selectedApp.value ? `${window.location.origin}/portal/${selectedApp.value.slug}` : ''
)

async function copyPortalLink () {
  const url = portalUrl.value
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url)
    } else {
      const ta = document.createElement('textarea')
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0'
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
    }
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  } catch (e) {
    error.value = `Could not copy automatically. Link: ${url}`
  }
}

async function loadApps () {
  const { data } = await appDocumentService.getApps()
  apps.value = data.apps
  if (apps.value.length && !selectedApp.value) selectApp(apps.value[0])
}

async function selectApp (app) {
  selectedApp.value = app
  const { data } = await appDocumentService.getDocuments(app.id)
  documents.value = data.app_documents
}

function onFile (e) { form.value.file = e.target.files[0] || null }

async function createApp () {
  error.value = null
  try {
    const { data } = await appDocumentService.createApp(newApp.value)
    apps.value.push({ ...data.app })
    newApp.value = { name: '', description: '' }
    showAddApp.value = false
    selectApp(data.app)
  } catch (e) { error.value = errMsg(e) }
}

async function createDocument () {
  if (!selectedApp.value) return
  saving.value = true
  error.value = null
  try {
    await appDocumentService.createDocument(selectedApp.value.id, form.value)
    form.value = blankForm()
    await refresh()
  } catch (e) { error.value = errMsg(e) } finally { saving.value = false }
}

async function togglePublish (doc) {
  try {
    await appDocumentService.updateDocument(selectedApp.value.id, doc.id, { published: !doc.published })
    await refresh()
  } catch (e) { error.value = errMsg(e) }
}

async function removeDocument (doc) {
  if (!confirm(`Delete "${doc.title}"?`)) return
  try {
    await appDocumentService.deleteDocument(selectedApp.value.id, doc.id)
    await refresh()
  } catch (e) { error.value = errMsg(e) }
}

async function refresh () {
  const id = selectedApp.value.id
  const [appsRes, docsRes] = await Promise.all([
    appDocumentService.getApps(),
    appDocumentService.getDocuments(id)
  ])
  apps.value = appsRes.data.apps
  selectedApp.value = apps.value.find(a => a.id === id) || selectedApp.value
  documents.value = docsRes.data.app_documents
}

function errMsg (e) {
  return e.response?.data?.errors?.join(', ') || e.response?.data?.error || e.message
}

onMounted(loadApps)
</script>
